import React from "react";
import PropTypes from "prop-types";
import { Button } from "rsuite";
import {
  getIdTokenClaims,
  trackHeapEvent,
} from "@hoteltradergit/ht-telemetry-ui-lib";
// Shared Action Center icons, recoloured for this card in its SCSS.
import TrendingUpIcon from "../../../../assets/img/action-center/trending-up.svg?react";
import GiftIcon from "../../../../assets/img/action-center/present.svg?react";
import CalendarIcon from "../../../../assets/img/action-center/calendar.svg?react";
import ChevronRightIcon from "../../../../assets/img/action-center/chevron-right-white.svg";
import useHeapCardViewed from "../../../../hooks/useHeapCardViewed";
import { Can, P } from "../../../../rbac";
import { BLACK_FRIDAY_COPY } from "../../../../constants/blackFriday";
import { getBlackFridayRows } from "../../../../utils/blackFriday";
import "./BlackFridayCard.scss";

const CARD_TYPE = "black_friday";

/**
 * Presentation for the Black Friday v2 card (EO-1891, Figma 1:28002). Same
 * contract as LlmCard — LlmActionCard owns the Create Promo drawer and the
 * card-state writes — but the campaign's own dark design.
 */
const BlackFridayCard = ({
  card,
  onDismiss,
  onCta,
  cardPosition,
  hotelName,
}) => {
  const cardRef = useHeapCardViewed(CARD_TYPE, cardPosition, hotelName);
  const rows = getBlackFridayRows(card);

  const heapProps = {
    card_type: CARD_TYPE,
    card_position: cardPosition,
    page_section: "action_center",
    hotel_name: hotelName || null,
    cta: card?.cta || null,
    user_type: getIdTokenClaims()?.["custom:userType"] || null,
  };

  const handleMaybeLater = () => {
    trackHeapEvent("Recommendation Card Dismissed", heapProps);
    onDismiss?.();
  };

  const handleCta = () => {
    trackHeapEvent("Recommendation Card CTA Clicked", heapProps);
    onCta?.(card);
  };

  return (
    <div ref={cardRef} className="bf-card">
      <div className="bf-card__body">
        <div className="bf-card__pill">
          <TrendingUpIcon aria-hidden="true" className="bf-card__pill-icon" />
          <span>{card?.cardType || BLACK_FRIDAY_COPY.cardPill}</span>
        </div>

        <div className="bf-card__text">
          <h3 className="bf-card__title">{card?.headline}</h3>
          <p className="bf-card__description">{card?.body}</p>
        </div>

        <div className="bf-card__rows">
          {rows.map((row) => (
            <div key={row.label} className="bf-card__row">
              <span className="bf-card__row-label">
                {row.isDiscount ? (
                  <GiftIcon
                    aria-hidden="true"
                    className="bf-card__row-icon bf-card__row-icon--stroke"
                  />
                ) : (
                  <CalendarIcon
                    aria-hidden="true"
                    className="bf-card__row-icon bf-card__row-icon--fill"
                  />
                )}
                {row.label}
              </span>
              <span className="bf-card__row-value">{row.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bf-card__actions">
        <Button
          appearance="link"
          className="bf-card__later"
          onClick={handleMaybeLater}
          data-heap-id="black-friday-card-maybe-later"
        >
          {BLACK_FRIDAY_COPY.maybeLater}
        </Button>
        {card?.cta && (
          <Can permission={P.Home.ActionCenter.CreatePromo}>
            <Button
              className="bf-card__cta"
              onClick={handleCta}
              data-heap-id="black-friday-card-create-promo"
            >
              {card.cta}
              <img
                src={ChevronRightIcon}
                alt=""
                className="bf-card__cta-icon"
              />
            </Button>
          </Can>
        )}
      </div>
    </div>
  );
};

BlackFridayCard.propTypes = {
  card: PropTypes.shape({
    headline: PropTypes.string,
    body: PropTypes.string,
    cta: PropTypes.string,
    promoObjectDr: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  }).isRequired,
  onDismiss: PropTypes.func,
  onCta: PropTypes.func,
  cardPosition: PropTypes.number,
  hotelName: PropTypes.string,
};

export default BlackFridayCard;
